import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient } from "@/helpers/api.js"
import { useGlobalStore } from '@/stores/GlobalStore'
import { useInventoryStore } from '@/stores/InventoryStore'
import { useUserStore } from '@/stores/UserStore'
import { useCreateBatchStore } from "@/stores/CreateBatchStore"
import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'
import {
  BATCH_CARD_COLUMN_OPTIONS,
  VIEW_OPTIONS,
  BATCH_DETAILS_VIEW_OPTIONS,
  DISCLOSURES,
  DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS,
  BATCH_TYPE_LABEL
} from '@/constants'
import { useQRCode } from '@vueuse/integrations/useQRCode'

export const useBatchStore = defineStore('BatchStore', {
  state: () => {
    return {
      apiBaseUrl: import.meta.env.VITE_API_HOST,
      batchProcessorBaseUrl: import.meta.env.VITE_BATCH_PROCESSOR_API_HOST,
      gridApi: null,

      batches: [],
      pagination: null,

      indexLoading: true,

      batchKind: '',

      selectedBatch: null,
      selectedBatchId: null,
      selectedBatchName: null,
    
      uploads: [],
      countOfUploadedImagesFromMobile: 0,

      notificationStatus: {},
      
      columnOptions: [...BATCH_CARD_COLUMN_OPTIONS],

      viewOptions: [...VIEW_OPTIONS],
      viewOption: { ...VIEW_OPTIONS[0] },
      selectedBatchDetailsViewOption: BATCH_DETAILS_VIEW_OPTIONS[0].value,

      disclosures: { ...DISCLOSURES },

      isAllFilterItem: false,

      openedPanel: false,

      settings: {
        name: '',
        ...DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS
      },

      batchType: 'sports',

      recentlyUploadedImagesIds: [],

      inspectorExpandedErrorBlock: false,

      rowNode: null,

      qrcodeLink: `${import.meta.env.VITE_MAIN_SITE}/mobile-scan`,
      otpPassword: '',
      generateOtpPasswordLoader: false,
    }
  },

  getters: {
    selectedViewOption() {
      if (!this.viewOption) return
      const userStore = useUserStore()
      const savedBatchDetailsViewOption = JSON.parse(localStorage.getItem('batchDetailsViewOption'))

      return (savedBatchDetailsViewOption && savedBatchDetailsViewOption[userStore.user?.id]) || this.viewOption
    },
  },

  actions: {
    setBatchStoreFields(fields) {
      for (const [field, value] of Object.entries(fields)) {
        this[field] = value
      }
    },

    updateViewOptions(availableViewOptions) {
      if (availableViewOptions.length) {
        this.viewOptions = VIEW_OPTIONS.reduce((acc, item) => {
          if (availableViewOptions.includes(item.value)) {
            acc.push(item)
          }
          return acc
        }, [])

        this.viewOptions = [VIEW_OPTIONS[0], ...this.viewOptions]
      } else {
        this.viewOptions = []
      }
    },

    updateColumnOption(option) {
      const userStore = useUserStore()

      this.columnOptions = this.columnOptions.reduce((acc, item) => {
        item.field === option.field ? acc.push({...item, checked: !item.checked}) :  acc.push(item)

        return acc
      }, [])

      const displayColumns = JSON.parse(localStorage.getItem("display_columns")) || {}
      localStorage.setItem("display_columns", JSON.stringify({ ...displayColumns, [userStore.user.id]: this.columnOptions }))
    },

    updateSelectedBatch({data = this.selectedBatch, config = {}}) {
      const configStore = useConfigStore()
      const configuration = configStore.config || config

      if (data) {
        this.selectedBatch = data
        this.selectedBatchId = data.id
      }

      if (data && configuration && Object.keys(configuration).length) {
        const sportsAndGamesBasedOnType = data.type === 'tcg'
          ? configuration.games.find(item => item.collx_type_id === data.collx_type_id)
          : configuration[data.type].find(item => item.collx_type_id === data.collx_type_id)

        const titleTemplateKey = Object.keys(configuration.titleTemplates).find(key => configuration.titleTemplates[key].id === data.title_template_id)
        const descriptionTemplateKey = Object.keys(configuration.descriptionTemplates).find(key => configuration.descriptionTemplates[key].id === data.description_template_id)
        this.settings = {
          name: data.name,
          titleTemplates: configuration.titleTemplates[titleTemplateKey] ||  { id: null, name: 'Default CDP Template' },
          descriptionTemplates: configuration.descriptionTemplates[descriptionTemplateKey] || { id: null, name: 'Default CDP Template' },
          sportsAndGamesBasedOnType: sportsAndGamesBasedOnType || { collx_type_id: null, name: data.type === 'tcg' ? 'Assorted Games' : 'Assorted Sports' },
          batchTypes: { name: BATCH_TYPE_LABEL[data.type], id: data.type },
        }

        this.batchType = data.type
      }
    },

    updateToDefault() {
      const createBatchStore = useCreateBatchStore()
      const cardStore = useCardStore()

      this.viewOptions = VIEW_OPTIONS
      this.columnOptions = BATCH_CARD_COLUMN_OPTIONS
      this.selectedBatchDetailsViewOption = BATCH_DETAILS_VIEW_OPTIONS[0].value
      this.selectedBatch = null
      this.selectedBatchId = null
      this.settings = {
        name: '',
        ...DEFAULT_SELECTED_VALUES_FOR_DROPDOWNS
      }

      cardStore.selectedCardId = null
      cardStore.pinnedCardData = {}
      cardStore.itemFilters = {}
      cardStore.selectedRanges = null
      cardStore.selectedCards = {}

      createBatchStore.batchType = 'sports'
      createBatchStore.selectedLockSet = null
    },

    async fetchBatches({ page = 1, status = null, query = 'createdAt.desc' }) {
      const inventoryStore = useInventoryStore()

      if (inventoryStore.currentFetchController) {
        inventoryStore.currentFetchController.abort()
      }

      this.indexLoading = true

      let url = `/batches?page=${page}&sortBy=${query}`
      if (status) {
        url += `&status=${status}`
      }

      try {
        const response = await apiClient().get(url)

        const data = response.data

        this.batches = data.items

        this.pagination = {
          perPage: 25,
          currentPage: data.current_page,
          totalItems: data.total_items,
          nextPage: data.next_page ? data.current_page + 1 : null,
          prevPage: data.current_page === 1 ? null : data.current_page - 1,
          totalPages: data.total_pages
        }

        return data.items
      } catch (e) {
        console.log('fetchBatches error', e)
      } finally {
        this.indexLoading = false
      }
    },

    fetchPrevPage() {
      if (this.pagination?.prevPage) {
        this.fetchBatches(this.pagination?.prevPage)
      }
    },
    fetchNextPage() {
      if (this.pagination?.nextPage) {
        this.fetchBatches(this.pagination?.nextPage)
      }
    },

    async fetchBatch(batchId) {
      const createBatchStore = useCreateBatchStore()
      try {
        const { data } = await apiClient().get(`/batches/${batchId}`)

        console.log('fetchBatch data', data)
        
        createBatchStore.batchInfo = {
          collx_type_id: data.collx_type_id
        }

        this.updateSelectedBatch({ data })
      } catch (e) {
        console.error('fetchBatch error', e)
      }
    },

    async updateBatch(body) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      try {
        const { data } = await apiClient().put(`/batches/${this.selectedBatchId}`, body)

        this.batches = this.batches.reduce((acc, element) => {
          if (element.id === data.id) {
            acc.push({ ...element, ...body })
          } else {
            acc.push(element)
          }

          return acc
        }, [])

        if (this.selectedBatch) {
          this.updateSelectedBatch({ data: {...data} })
        }
      } catch (error) {
        console.log('updateBatch error', error)
      }
    },

    toggleBatchStatus(batch) {
      this.selectedBatchId = batch.id
      this.updateBatch({ status: batch.status === 'open' ? 'closed' : 'open' })
    },

    async reprocessBatch() {
      const configStore = useConfigStore()
      const cardStore = useCardStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()
      const globalStore = useGlobalStore()

      try {
        globalStore.cellsLoading = true

        if (this.selectedBatchDetailsViewOption !== 'Overview') {
          cardStore.cardsLoading = true
        }

        const data = await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/reprocess`)
      } catch (error) {
        console.log('reprocessBatch error', error)
      }
    },

    async addBannerToBatch() {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()

      try {
        await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/add-banner?banner=${userStore.user.banner_image}&location=${userStore.user?.banner_placement || 'top'}`)
      } catch (error) {
        console.error('addBannerToBatch error', error)
      }
    },

    async removeBannerFromBatch() {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const userStore = useUserStore()

      try {
        await apiClient(true).post(`/users/${userStore.user.id}/batches/${this.selectedBatchId}/remove-banner?banner=${userStore.user.banner_image}`)
      } catch (error) {
        console.error('removeBannerFromBatch error', error)
      }
    },

    async removeBatch() {
      try {
        await apiClient().delete(`/batches/${this.selectedBatchId}`)
        this.batches = this.batches.reduce((acc, item) => {
          if (item.id !== this.selectedBatchId) {
            acc.push(item)
          }

          return acc
        }, [])
      } catch (error) {
        console.log('removeBatch error', error)
      }
    },

    async getOtpPassword(batchId) {
      try {
        this.generateOtpPasswordLoader = true
        const { data } = await apiClient().post(`otp-links`, {
          redirect_to: `/mobile-scan/${batchId}`
        })

        if (data?.ok) {
          const splitLoginUrl = data.otp_login_url.split('/')
          const url = `${import.meta.env.VITE_MAIN_SITE}/otp-links/${splitLoginUrl[splitLoginUrl.length - 1]}`

          this.qrcodeLink = useQRCode(url)
        }

        this.generateOtpPasswordLoader = false
      } catch (error) {
        this.generateOtpPasswordLoader = false
        console.log('getOtpPassword error', error)
      }
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBatchStore, import.meta.hot))
}
